import { useCallback } from 'react';

import { UserManage, UserService } from 'openapi';

import { showSuccessToastMessage } from 'components/shared/Toast/Toast';

import { useTranslations } from 'context/TranslationContext';

import { useErrorResultHandler } from 'hooks/useErrorResultHandler';
import { useInterceptor } from 'hooks/useInterceptor';

import { HttpStatusCodes } from 'utils/enums/HttpStatusCodes';

export const useUserController = () => {
  const { interceptRequest } = useInterceptor();

  const {
    login,
    register,
    getApproversByCompanyId,
    getAllUsers,
    createUser,
    deleteUsersByIds,
    updateUserById,
    getAllUsersAndSuperUsersByCompanyId
  } = UserService;

  const { handleErrorResponse } = useErrorResultHandler();
  const { translate } = useTranslations();

  const userRegister = async () => {
    try {
      await register();
      const response = await login();

      return response;
    } catch (error: any) {
      return error;
    }
  };

  const userLogin = useCallback(async () => {
    try {
      const response = await login();

      return response.data;
    } catch (error: any) {
      if (error.body.error.status === HttpStatusCodes.ExpectationFailed) {
        const response = await userRegister();
        return response.data;
      }

      handleErrorResponse(error.body.error.status);

      return error;
    }
  }, []);

  const getApprovers = useCallback(
    async (id: number) => interceptRequest(getApproversByCompanyId, {}, id),
    [getApproversByCompanyId]
  );

  const addUser = useCallback(
    async (user: UserManage) =>
      interceptRequest(
        createUser,
        {
          onSuccess: () =>
            showSuccessToastMessage(translate('messages.successfullyCreated'))
        },
        user
      ),
    [createUser]
  );

  const updateUser = useCallback(
    (id: number, user: UserManage) =>
      interceptRequest(updateUserById, {}, id, user),
    [updateUserById]
  );

  const deleteUsers = useCallback(
    (userIds: number[]) => interceptRequest(deleteUsersByIds, {}, userIds),
    [deleteUsersByIds]
  );

  const getUsers = useCallback(async () => {
    return interceptRequest(getAllUsers, {});
  }, []);

  const getUsersByCompanyId = useCallback(
    async (companyId: number) =>
      interceptRequest(getAllUsersAndSuperUsersByCompanyId, {}, companyId),
    [getAllUsersAndSuperUsersByCompanyId]
  );

  return {
    userLogin,
    userRegister,
    getApprovers,
    getUsers,
    addUser,
    updateUser,
    deleteUsers,
    getUsersByCompanyId
  };
};
